import type { HTMLAttributes, FC, Ref } from "react";
import { AnimatePresence, motion } from "motion/react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { transitionDurations } from "../../theme/transitionDurations";
import { revealStyle } from "./Reveal.css";

export type RevealProps = HTMLAttributes<HTMLDivElement> &
  RecipeVariants<typeof revealStyle> & {
    /**
     * Whether the animation should be initial.
     */
    initial?: boolean;
    /**
     * The duration of the presence animation in seconds.
     */
    duration?: number;
    /**
     * The delay before the animation starts in seconds.
     */
    animateDelay?: number;
    /**
     * The delay before the exit animation starts in seconds.
     * This is useful for animations that should be visible for a short time before they are removed.
     */
    exitDelay?: number;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A component that animates the children when they are visible.
 */
export const Reveal: FC<RevealProps> = ({
  duration = transitionDurations.m,
  initial = true,
  animateDelay,
  exitDelay,
  renderAs,
  gap,
  className,
  children,
  ref,
  ...props
}) => (
  <AnimatePresence>
    {children && (
      <motion.div
        ref={ref}
        className={mergeClasses(className, revealStyle({ renderAs, gap }))}
        transition={{ duration }}
        initial={initial ? { opacity: 0 } : undefined}
        animate={{ opacity: 1, transition: { delay: animateDelay } }}
        exit={{ opacity: 0, transition: { delay: exitDelay } }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);
